import {
  IconButton,
  Sheet,
  SheetBody,
  SheetContent,
  SheetTitle,
} from "@suraasa/placebo-ui"
import { useMediaQuery } from "@uidotdev/usehooks"
import clsx from "clsx"
import { AuthManager } from "features/Authentication/AuthManager"
import { useMotionValueEvent, useScroll } from "framer-motion"
import { Xmark } from "iconoir-react"
import { useEffect, useState } from "react"
import { buildUserName } from "utils/helpers"

import { usePgCTLOfferLetter } from "../usePgCTLOfferLetter"
import FloatingBar from "./FloatingBar"
import LetterContent from "./LetterContent"
import TitleBar from "./TitleBar"

const PGCTL_OFFER_LETTER_TOP_BAR = "pgctl-offer-letter-top-bar"

const OfferLetterSheet = ({
  open,
  handleClose,
}: {
  open: boolean
  handleClose: () => void
}) => {
  const { offerLetter, markViewed, hasViewedOfferLetter } =
    usePgCTLOfferLetter()

  const [sheetBodyRef, setSheetBodyRef] = useState<HTMLDivElement | null>(null)
  const { scrollY } = useScroll({ container: { current: sheetBodyRef } })
  const [isStuck, setIsStuck] = useState(true)
  const isLgUp = useMediaQuery("only screen and (min-width : 1280px)")

  const userDetails = AuthManager.getPlatformAuth()?.user
  const fullName = userDetails ? buildUserName(userDetails) : ""

  useMotionValueEvent(scrollY, "change", latest => {
    const topBarElement = document.getElementById(PGCTL_OFFER_LETTER_TOP_BAR)
    if (!topBarElement) return

    const topBarHeight = Math.round(topBarElement.clientHeight)
    const elementScrolled = latest < topBarHeight ? 0 : latest - topBarHeight

    if (elementScrolled >= 10) {
      setIsStuck(false)
    } else {
      setIsStuck(true)
    }
  })

  const scrollToEnd = () => {
    if (sheetBodyRef) {
      sheetBodyRef.scrollTo({
        top: sheetBodyRef.scrollHeight,
        behavior: "smooth",
      })
    }
  }

  useEffect(() => {
    if (!hasViewedOfferLetter && open) {
      markViewed.offerLetter()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasViewedOfferLetter, open])

  if (!offerLetter) return null

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        height={100}
        className={clsx("max-w-none bg-surface-100", {
          "pt-2.5 px-0 pb-0": isLgUp,
          "p-0": !isLgUp,
        })}
      >
        {!isLgUp && (
          <SheetTitle
            className={clsx(
              "flex flex-col bg-transparent px-2 py-[15px] transition-all sm:px-7 sm:py-2.25",
              {
                "bg-white shadow-[0px_6px_15px_0px_#0000000D]": !isStuck,
                "bg-transparent": isStuck,
              }
            )}
          >
            <div className="flex items-center justify-between ">
              <div
                id={PGCTL_OFFER_LETTER_TOP_BAR}
                className="sticky top-0 flex items-center justify-between "
              >
                <img
                  className="h-4.5 w-[122px]"
                  src="https://assets.suraasa.com/logos/v2/full-dark.png"
                  alt="suraasa-logo"
                />
              </div>
              <IconButton
                color="black"
                size="sm"
                className={clsx("transition-all", {
                  "bg-onSurface-900/5 hover:bg-onSurface-900/15": isStuck,
                })}
                onClick={handleClose}
              >
                <Xmark />
              </IconButton>
            </div>
          </SheetTitle>
        )}

        <SheetBody
          style={{
            backgroundImage: `
  radial-gradient(ellipse at 0% 80%, rgba(200, 166, 235, 0.8) 0, transparent 30%),
  radial-gradient(ellipse at 40% 80%, rgba(255, 219, 137, 1) 0, transparent 40%),
  radial-gradient(ellipse at 80% 80%, rgba(138, 189, 255, 0.8) 0, transparent 30%)`,
          }}
          className="pb-10 pt-0"
          ref={setSheetBodyRef}
        >
          {isLgUp && (
            <div
              id={PGCTL_OFFER_LETTER_TOP_BAR}
              className="sticky top-0 flex items-center justify-between px-0  pb-4.5 pt-[30px] sm:px-7 sm:pb-6 sm:pt-4.5"
            >
              <img
                className="h-4.5 w-[122px]"
                src="https://assets.suraasa.com/logos/v2/full-dark.png"
                alt="suraasa-logo"
              />
              <IconButton
                color="black"
                className="bg-onSurface-900/5 hover:bg-onSurface-900/15"
                size="sm"
                onClick={handleClose}
              >
                <Xmark />
              </IconButton>
            </div>
          )}

          <div className="relative mx-auto max-w-[801px] rounded-xl bg-white pb-5 pt-[26px] shadow-[0px_6px_15px_0px_#0000000D]">
            <TitleBar
              offerLetter={offerLetter}
              isLgUp={isLgUp}
              isStuck={isStuck}
            />
            <LetterContent offerLetter={offerLetter} fullName={fullName} />
            <FloatingBar isStuck={isStuck} scrollToEnd={scrollToEnd} />
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

export default OfferLetterSheet
